import { useMemo } from 'react'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { getAllSpecificPriceRules, getAllWorkingCenters, getConfigParam, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../utils/globals/dictionary'
import { TwinParameters } from '@teinor/erp/types/company/config/parameter'
import OrderBuyLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout'
import useBuyOrderHeaderURLs from '../../../../../../../../../utils/hooks/buys/orders/useBuyOrderHeaderURLsN'
import useOrderRowRenderer from '../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import { BuyOrderStatusInversed } from '@teinor/erp/types/company/order/buy/buyOrder'
import { BuyOrderProductModelTypeExtended } from '@teinor/erp/types/company/order/buy/buyOrder/buyOrderProduct'
import { checkIfOrderIsOkForSend } from '../../../../../../../../../utils/globals/product/uniqueNumber'
import BuyOrderWaitingResponseITHeader from './BuyOrderWaitingResponseITHeader'
import BuyOrderWaitingResponseStepHeaderDecider from './BuyOrderWaitingResponseHeader'
import { CustomRenderPriceWithMargin, CustomRenderSpecificPrice } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/CustomRenders'

interface BuyOrderWaitingResponseProps extends InputTableLayoutStepProps { }

const BuyOrderWaitingResponse: React.FC<BuyOrderWaitingResponseProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useBuyOrderWaitingResponseLogic({ completeData })
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    const buyToDeliveryNoteFinished = getConfigParam('buyToDeliveryNoteFinished') as TwinParameters['buyToDeliveryNoteFinished']
    const buyToInvoiceFinished = getConfigParam('buyToInvoiceFinished') as TwinParameters['buyToInvoiceFinished']
    const nextStepNotFinished = (buyToDeliveryNoteFinished === 'off' || buyToInvoiceFinished === 'off') ? true : false
    const workingCenters = getAllWorkingCenters()
    const orderHeaderURLs = useBuyOrderHeaderURLs()
    const specificPriceRules = getAllSpecificPriceRules()

     if (!completeData) {
        return null
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & BuyOrderProductModelTypeExtended> = [
        {
            id: 'selected',
            dataKey: 'selected',
            label: '',
            simpleLabel: t('selectLine', 'Seleccionar línea'),
            width: 50,
            requiredColumn: true,
            component: 'ITCheckbox',
            headerClassName: 'checkbox_header_filter',
            extraProps: { nextStepNotFinished, disabled: nextStepNotFinished ? false : !checkIfOrderIsOkForSend(completeData, t) },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: { 'unprocessed': { name: t('unprocessed', 'Sin procesar') }, 'processed': { name: t('processed', 'Procesados') } }, label: t('show', 'Mostrar') },
            }
        },
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchSupplierProductInputReference',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchSupplierProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchSupplierProductInputReferenceAndName',
            field: 'referenceAndName',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            minWidth: 180,
            SupplierId: completeData?.header?.SupplierId,
            className: 'quote_order_reference_name'
        },
        {
            id: 'supplier_reference',
            dataKey: 'supplier_reference',
            label: t('supplierRefShort', 'Ref. Prov.'),
            simpleLabel: t('supplierRefLong', 'Referencia Proveedor'),
            toolTip: t('supplierRefLong', 'Referencia Proveedor'),
            component: 'ITRectangularInput',
            typeOnChange: 'onChangeFetch',
            width: 130,
            hideColumnByDefault: true
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className='twin_elipsis' />
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            simpleLabel: t('cost', 'Coste'),
            label: () => <div className='text-right w-full mr-10'>{t('cost', 'Coste')}</div>,
            component: 'ITInputPrice',
            typeOnChange: 'onChangeTotalsFetch',
            width: 120,
            sortable: true
        },
        {
            id: 'Product.SpecificPriceRuleId',
            dataKey: 'Product.SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || specificPriceRules?.defaultSpecificPriceRuleId)
                return <span>{t('pvp', 'PVP') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('sellPrice', 'Precio de venta'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderSpecificPrice specificPriceRules={specificPriceRules} allRowData={allRowData} />,
            hideColumnByDefault: true
        },
        {
            id: 'Product.SpecificPriceRuleIdActual',
            dataKey: 'Product.SpecificPriceRuleId',
            label: t('costPriceWithMarginShort', 'PCM'),
            simpleLabel: t('costPriceWithMargin', 'Precio coste con margen'),
            toolTip: t('costPriceWithMargin', 'Precio coste con margen'),
            customRender: (parameterValue, allRowData) => <CustomRenderPriceWithMargin allRowData={allRowData} CurrencyId={completeData?.header?.CurrencyId} />,
            hideColumnByDefault: true
        },
        {
            id: 'updateSupplierPrice',
            dataKey: 'updateSupplierPrice',
            label: t('updatePriceShort', 'Act. precio'),
            simpleLabel: t('updatePrice', 'Actualizar precio'),
            toolTip: t('updatePrice', 'Actualizar precio'),
            component: 'ITOrderBooleanRectangle',
            typeOnChange: 'onChangeFetch',
            width: 120,
            translations: { positive: t('update', 'Actualizar'), negative: t('notUpdate', 'No actualizar') },
        },
        {
            id: 'updateCostPriceAndMargin',
            dataKey: 'updateCostPriceAndMargin',
            label: t('updateCostShort', 'Act. coste'),
            simpleLabel: t('updateCostMargin', 'Actualizar coste y margen'),
            toolTip: t('updateCostMargin', 'Actualizar coste y margen'),
            component: 'ITOrderBooleanRectangle',
            typeOnChange: 'onChangeFetch',
            width: 120,
            translations: { positive: t('update', 'Actualizar'), negative: t('notUpdate', 'No actualizar') },
        },
        {
            id: 'margin',
            dataKey: 'margin',
            label: t('margin', 'Margen'),
            component: 'ITInputPercentage',
            typeOnChange: 'onChangeTotalsFetch',
            hideColumnByDefault: true,
            width: 100,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantityServedShort2', 'Cant. serv.'),
            width: 110,
            component: 'ITInputQuantity',
            typeOnChange: 'onChangeTotalsFetch',
            simpleLabel: t('quantityServed', 'Cantidad servida'),
            toolTip: t('quantityServed', 'Cantidad servida'),
            skipMinQuantity: true
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            component: 'ITInputQuantity2',
            width: 115,
            typeOnChange: 'onChangeFetch',
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            hideColumnByDefault: true,
            width: 110,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'OrderAccountId',
            dataKey: 'OrderAccountId',
            label: t('chapter', 'Capítulo'),
            customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'saveRest',
            dataKey: 'saveRest',
            label: t('saveRest', 'Guardar resto'),
            width: 110,
            component: 'OrderSaveRemainingProduct',
            typeOnChange: 'onChangeFetch',
            hideColumnByDefault: true
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'N. único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProduct',
            hideColumnByDefault: true
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            component: 'ITInputSelect',
            items: workingCenters?.[completeData?.header?.WorkingCenterId]?.dictWarehouses || {},
            typeOnChange: 'onChangeTotalsFetch',
            minWidth: 110,
            hideColumnByDefault: true
        },
        {
            id: 'desiredQuantity',
            dataKey: 'desiredQuantity',
            label: t('leftQuantityShort', 'Cant. rest.'),
            simpleLabel: t('leftQuantityLong', 'Cantidad restante'),
            toolTip: t('leftQuantityLong', 'Cantidad restante'),
            width: 120,
            component: 'ITInputQuantity',
            readOnlyFnc: () => true
        },
        {
            id: 'discount',
            dataKey: 'discount',
            label: t('discount', 'Descuento'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowCost', 'Coste Línea')}</div>,
            simpleLabel: t('rowCost', 'Coste Línea'),
            component: 'ITDisplayFinalPrice',
            hideColumnByDefault: true,
            with_vat: false,
            width: 120
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('costTotal', 'Coste Total')}</div>,
            simpleLabel: t('costTotal', 'Coste Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalCostWithTaxShort', 'Coste Total Imp.')}</div>,
            simpleLabel: t('totalCostWithTaxShort', 'Coste Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: ()=><div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip:  t('totalWithComplements', 'Total con complementos') 
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitCostWithComplements', 'Coste unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitCostWithComplements', 'Coste unitario con complementos'),
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
    ]
    return (
        <OrderBuyLayout modelId='BuyOrderId' name='BuyOrderWaitingResponse' translations={{ headerTitle: t('buyOrder', 'Pedido') }}  buttonSave={
            permissionCheck(getUserPermissions('buy.order'), 'update') || permissionCheck(getUserPermissions('buy.order'), 'delete') ?
                <BuyOrderWaitingResponseStepHeaderDecider orderStatus={completeData?.header.status} isReadOnly={true} OrderLogs={completeData?.header?.OrderLogs} onSubmit={getCompleteData} data={completeData as OrderData} />
            : undefined
            } InputTableHeaderChildren={BuyOrderWaitingResponseITHeader} columns={columns} isReadOnly={true} {...{ rowRenderer, inputTableURLs, orderHeaderURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges,  orderStatusDict: BuyOrderStatusInversed}} />
    )
}

interface BuyOrderWaitingResponseLogicProps {
    completeData: OrderData | null
}

export const useBuyOrderWaitingResponseLogic = ({ completeData }: BuyOrderWaitingResponseLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData })

    return { rowRenderer }
}

export default BuyOrderWaitingResponse